// Terrain-aware "fastest path" for tactical units: a weighted A* over a nav
// grid rastered from the zone. Cells are classified once (static per zone):
// inside a building or outside the zone circle = BLOCKED (impassable), near a
// street = STREET (cheap), otherwise OPEN (off-street, expensive). The
// off-street multiplier is applied at query time, not baked into the grid, so
// the harness's live slider re-routes without rebuilding.
//
// The result: units follow roads when roads win but cut diagonally across a
// lot when the shortcut actually beats going around — the street-vs-offstreet
// cost knob drives routing, not just per-step speed. Buildings are walked
// around, never through.
//
// Grid build is a one-time cost per zone; each path query is a single A* over
// a few-thousand-cell grid (sub-millisecond), so re-routing live during a drag
// is fine.

import { distanceInMeters } from "../road-graph.mjs";
import { distanceToSegmentMeters } from "./fog-geometry.mjs";
import { pointInBuildings } from "./line-of-sight.mjs";
import { OFF_STREET_COST_MULTIPLIER, STREET_THRESHOLD_METERS } from "./tactical-movement.mjs";

export const CELL_SIZE_METERS = 4;
// door: a walkable opening in a base wall (see the base-defense options below),
// tagged with the doorway's edgeKey so a caller can gate it by HP at query time.
export const CELL = Object.freeze({ blocked: 0, street: 1, open: 2, door: 3 });

const METERS_PER_LAT = 111320;

// --- Grid construction ----------------------------------------------------

// Base-defense options (all optional; absent = a plain raid grid):
//   interior(coordinate) -> bool : cells inside the base are walkable at normal
//     speed, overriding the "every building is a solid obstacle" default.
//   wallSegments: [{ a, b }]      : world-space wall segments, rastered to a
//     blocked band so units route around, never through, a wall.
//   doorSegments: [{ a, b, edgeKey }] : world-space doorway openings, tagged
//     CELL.door + a per-cell edgeKey so findTerrainPath can gate them by HP.
export function buildNavGrid(zone, {
  cellSizeMeters = CELL_SIZE_METERS,
  streetThresholdMeters = STREET_THRESHOLD_METERS,
  extractionBandMeters = 0,
  interior = null,
  wallSegments = null,
  doorSegments = null,
} = {}) {
  const center = zone.centerCoordinate;
  const radius = zone.radiusMeters;
  // The grid extends past the supply-run circle by the extraction band so units can
  // walk onto the band's streets (the green "exit lanes") — but only streets
  // are walkable out there, never off-street open ground (see below).
  const outerRadius = radius + extractionBandMeters;
  const metersPerLon = Math.cos(center[1] * Math.PI / 180) * METERS_PER_LAT;
  const originLon = center[0] - outerRadius / metersPerLon;
  const originLat = center[1] - outerRadius / METERS_PER_LAT;
  const cols = Math.ceil((2 * outerRadius) / cellSizeMeters);
  const rows = cols;
  const classes = new Int8Array(cols * rows);

  const cellToCoord = (col, row) => [
    originLon + (col + 0.5) * cellSizeMeters / metersPerLon,
    originLat + (row + 0.5) * cellSizeMeters / METERS_PER_LAT,
  ];
  const coordToCell = (coordinate) => ({
    col: Math.floor((coordinate[0] - originLon) * metersPerLon / cellSizeMeters),
    row: Math.floor((coordinate[1] - originLat) * METERS_PER_LAT / cellSizeMeters),
  });

  // Corridor segments with padded bounding boxes, so per-cell street tests only
  // touch nearby segments instead of scanning every corridor.
  const padDegLat = streetThresholdMeters / METERS_PER_LAT;
  const padDegLon = streetThresholdMeters / metersPerLon;
  const segments = [];
  for (const corridor of zone.corridors.values()) {
    const line = corridor.coordinates;
    for (let i = 0; i < line.length - 1; i += 1) {
      const a = line[i];
      const b = line[i + 1];
      segments.push({
        a, b,
        minLon: Math.min(a[0], b[0]) - padDegLon, maxLon: Math.max(a[0], b[0]) + padDegLon,
        minLat: Math.min(a[1], b[1]) - padDegLat, maxLat: Math.max(a[1], b[1]) + padDegLat,
      });
    }
  }

  // Base walls/doors: bounding-boxed like the corridor segments so per-cell
  // proximity tests only touch nearby edges. A wall is rastered to a band about
  // one cell thick so the path smoother can't straight-line through it; the
  // door band is carved out of that (doors are tested first) to leave the
  // opening walkable. doorEdges maps a cell -> its doorway edgeKey (or "").
  const obstacleBand = cellSizeMeters * 0.7;
  const wallSegs = boxedSegments(wallSegments, obstacleBand, metersPerLon);
  const doorSegs = boxedSegments(doorSegments, obstacleBand, metersPerLon);
  const doorEdges = doorSegs ? new Array(cols * rows).fill("") : null;

  for (let row = 0; row < rows; row += 1) {
    for (let col = 0; col < cols; col += 1) {
      const coordinate = cellToCoord(col, row);
      const distance = distanceInMeters(coordinate, center);
      if (distance > outerRadius) { classes[row * cols + col] = CELL.blocked; continue; }

      // Base overrides, in precedence order: a wall blocks (and wins where it
      // meets a door, so a door flush to an outer wall can't punch a hole
      // through the perpendicular wall at their shared corner); the doorway's
      // remaining span is a walkable opening; otherwise the interior is walkable.
      if (wallSegs && nearAnySegment(coordinate, wallSegs, obstacleBand)) { classes[row * cols + col] = CELL.blocked; continue; }
      if (doorSegs) {
        const door = nearestSegment(coordinate, doorSegs, obstacleBand);
        if (door) { classes[row * cols + col] = CELL.door; doorEdges[row * cols + col] = door.edgeKey ?? ""; continue; }
      }
      if (interior && interior(coordinate)) { classes[row * cols + col] = CELL.street; continue; } // indoors: walkable at normal speed

      const inBuilding = pointInBuildings(coordinate, zone.buildings);
      const onStreet = nearAnySegment(coordinate, segments, streetThresholdMeters);
      let cls;
      if (distance > radius) cls = (onStreet && !inBuilding) ? CELL.street : CELL.blocked; // extraction band: streets only
      else if (inBuilding) cls = CELL.blocked;
      else cls = onStreet ? CELL.street : CELL.open;
      classes[row * cols + col] = cls;
    }
  }

  return { cols, rows, cellSizeMeters, classes, cellToCoord, coordToCell,
    classAt: (col, row) => (col < 0 || row < 0 || col >= cols || row >= rows ? CELL.blocked : classes[row * cols + col]),
    doorEdgeAt: (col, row) => (!doorEdges || col < 0 || row < 0 || col >= cols || row >= rows ? "" : doorEdges[row * cols + col]) };
}

// Wrap raw {a, b, edgeKey?} segments with padded lon/lat bounding boxes so a
// per-cell proximity test can skip far-away edges. Returns null for empty input.
function boxedSegments(rawSegments, padMeters, metersPerLon) {
  if (!rawSegments || rawSegments.length === 0) return null;
  const padDegLat = padMeters / METERS_PER_LAT;
  const padDegLon = padMeters / metersPerLon;
  return rawSegments.map(({ a, b, edgeKey }) => ({
    a, b, edgeKey,
    minLon: Math.min(a[0], b[0]) - padDegLon, maxLon: Math.max(a[0], b[0]) + padDegLon,
    minLat: Math.min(a[1], b[1]) - padDegLat, maxLat: Math.max(a[1], b[1]) + padDegLat,
  }));
}

// Like nearAnySegment but returns the matched segment (for its edgeKey).
function nearestSegment(coordinate, segments, thresholdMeters) {
  for (const segment of segments) {
    if (coordinate[0] < segment.minLon || coordinate[0] > segment.maxLon
      || coordinate[1] < segment.minLat || coordinate[1] > segment.maxLat) continue;
    if (distanceToSegmentMeters(coordinate, segment.a, segment.b) <= thresholdMeters) return segment;
  }
  return null;
}

function nearAnySegment(coordinate, segments, thresholdMeters) {
  for (const segment of segments) {
    if (coordinate[0] < segment.minLon || coordinate[0] > segment.maxLon
      || coordinate[1] < segment.minLat || coordinate[1] > segment.maxLat) continue;
    if (distanceToSegmentMeters(coordinate, segment.a, segment.b) <= thresholdMeters) return true;
  }
  return false;
}

// --- A* -------------------------------------------------------------------

function classCost(cls, offStreetMultiplier) {
  if (cls === CELL.street || cls === CELL.door) return 1; // a doorway is a cheap walkable opening
  if (cls === CELL.open) return offStreetMultiplier;
  return Infinity;
}

// Doors are walkable by default; a caller (e.g. an attacker who must breach an
// intact door) passes a predicate that only lets breached doors through.
const ALL_DOORS_PASSABLE = () => true;

// Nearest non-blocked cell to a target cell, spiralling outward — lets a click
// inside a building (or just outside the zone) resolve to the closest reachable
// spot instead of failing. `blockedExtra(col,row)` treats extra cells (an
// intact door the caller can't pass) as blocked too.
function snapToOpen(grid, cell, blockedExtra = null, maxRing = 40) {
  const isBlocked = (col, row) => grid.classAt(col, row) === CELL.blocked || (blockedExtra != null && blockedExtra(col, row));
  if (!isBlocked(cell.col, cell.row)) return cell;
  for (let ring = 1; ring <= maxRing; ring += 1) {
    for (let dcol = -ring; dcol <= ring; dcol += 1) {
      for (let drow = -ring; drow <= ring; drow += 1) {
        if (Math.max(Math.abs(dcol), Math.abs(drow)) !== ring) continue;
        const col = cell.col + dcol;
        const row = cell.row + drow;
        if (!isBlocked(col, row)) return { col, row };
      }
    }
  }
  return null;
}

const NEIGHBORS = [
  [1, 0], [-1, 0], [0, 1], [0, -1],
  [1, 1], [1, -1], [-1, 1], [-1, -1],
];

export function findTerrainPath(grid, fromCoord, toCoord, { offStreetMultiplier = OFF_STREET_COST_MULTIPLIER, doorPassable = ALL_DOORS_PASSABLE } = {}) {
  const doorBlocked = (col, row) => grid.classAt(col, row) === CELL.door && !doorPassable(grid.doorEdgeAt(col, row));
  const isBlocked = (col, row) => grid.classAt(col, row) === CELL.blocked || doorBlocked(col, row);
  const start = snapToOpen(grid, grid.coordToCell(fromCoord), doorBlocked);
  const goal = snapToOpen(grid, grid.coordToCell(toCoord), doorBlocked);
  if (!start || !goal) return { reachable: false, coordinates: [] };

  const { cols, rows, cellSizeMeters } = grid;
  const index = (col, row) => row * cols + col;
  const startIdx = index(start.col, start.row);
  const goalIdx = index(goal.col, goal.row);
  const goalCoord = grid.cellToCoord(goal.col, goal.row);

  if (startIdx === goalIdx) {
    return { reachable: true, coordinates: collapseCollinear([[...fromCoord], goalCoord]) };
  }

  const gScore = new Float64Array(cols * rows).fill(Infinity);
  const cameFrom = new Int32Array(cols * rows).fill(-1);
  const closed = new Uint8Array(cols * rows);
  gScore[startIdx] = 0;
  const open = new MinHeap();
  open.push(heuristic(start, goal, cellSizeMeters), startIdx);

  let found = false;
  // Closest reachable cell to the goal seen so far. When the goal itself can't
  // be reached (enclosed courtyard, walled-off, out of zone behind buildings),
  // we path here instead of failing — so the unit walks as far toward the
  // destination as the streets allow, and NEVER straight-lines through a
  // building.
  let bestIdx = startIdx;
  let bestHeuristic = heuristic(start, goal, cellSizeMeters);
  while (open.size()) {
    const currentIdx = open.pop();
    if (closed[currentIdx]) continue;
    closed[currentIdx] = 1;
    const col = currentIdx % cols;
    const row = Math.floor(currentIdx / cols);
    const h = heuristic({ col, row }, goal, cellSizeMeters);
    if (h < bestHeuristic) { bestHeuristic = h; bestIdx = currentIdx; }
    if (currentIdx === goalIdx) { found = true; break; }

    for (const [dcol, drow] of NEIGHBORS) {
      const ncol = col + dcol;
      const nrow = row + drow;
      if (ncol < 0 || nrow < 0 || ncol >= cols || nrow >= rows) continue;
      const ncls = grid.classAt(ncol, nrow);
      if (ncls === CELL.blocked || doorBlocked(ncol, nrow)) continue; // an intact door blocks like a wall
      const diagonal = dcol !== 0 && drow !== 0;
      // No cutting building corners: a diagonal needs both shared orthogonal
      // neighbours passable, else units slip through a diagonal wall gap.
      if (diagonal && (isBlocked(col + dcol, row) || isBlocked(col, row + drow))) continue;
      const stepMeters = (diagonal ? Math.SQRT2 : 1) * cellSizeMeters;
      const tentative = gScore[currentIdx] + stepMeters * classCost(ncls, offStreetMultiplier);
      const nIdx = index(ncol, nrow);
      if (tentative < gScore[nIdx]) {
        gScore[nIdx] = tentative;
        cameFrom[nIdx] = currentIdx;
        open.push(tentative + heuristic({ col: ncol, row: nrow }, goal, cellSizeMeters), nIdx);
      }
    }
  }

  // Goal reached, or (if not) the nearest cell we could actually walk to.
  const targetIdx = found ? goalIdx : bestIdx;
  if (targetIdx === startIdx) {
    // Can't get any closer without crossing a building — don't move.
    return { reachable: false, coordinates: [[...fromCoord]] };
  }

  const cells = [];
  for (let idx = targetIdx; idx !== -1; idx = cameFrom[idx]) cells.push(idx);
  cells.reverse();
  const coordinates = cells.map((idx) => grid.cellToCoord(idx % cols, Math.floor(idx / cols)));
  coordinates[0] = [...fromCoord]; // start from the unit's exact position
  return { reachable: true, partial: !found, coordinates: smoothPath(grid, collapseCollinear(coordinates), offStreetMultiplier, doorBlocked) };
}

// Sampled traversal cost of a straight segment (Infinity if it crosses a
// building or an impassable door). Shared by the smoother for both the shortcut
// and the original, so string-pulling never shortcuts through a blocked cell.
function segmentCost(grid, a, b, offStreetMultiplier, doorBlocked = null) {
  const meters = distanceInMeters(a, b);
  if (meters === 0) return 0;
  const steps = Math.max(1, Math.ceil(meters / (grid.cellSizeMeters / 2)));
  const perStep = meters / steps;
  let cost = 0;
  for (let i = 0; i < steps; i += 1) {
    const t = (i + 0.5) / steps;
    const point = [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    const cell = grid.coordToCell(point);
    const cls = grid.classAt(cell.col, cell.row);
    if (cls === CELL.blocked) return Infinity;
    if (cls === CELL.door && doorBlocked != null && doorBlocked(cell.col, cell.row)) return Infinity;
    cost += perStep * (cls === CELL.street || cls === CELL.door ? 1 : offStreetMultiplier);
  }
  return cost;
}

// Cost-aware string-pulling: collapse a run of grid-staircase points into a
// straight segment whenever the straight line is unobstructed AND no more
// expensive than the wiggly original. That kills the jitter without undoing
// cost-optimal detours (a street dip stays, since straightening it would cost
// more by crossing open ground).
export function smoothPath(grid, coordinates, offStreetMultiplier, doorBlocked = null) {
  if (coordinates.length <= 2) return coordinates;
  const prefix = [0];
  for (let i = 0; i < coordinates.length - 1; i += 1) {
    prefix.push(prefix[i] + segmentCost(grid, coordinates[i], coordinates[i + 1], offStreetMultiplier, doorBlocked));
  }
  const out = [coordinates[0]];
  let anchor = 0;
  while (anchor < coordinates.length - 1) {
    let next = anchor + 1;
    for (let j = coordinates.length - 1; j > anchor + 1; j -= 1) {
      const straight = segmentCost(grid, coordinates[anchor], coordinates[j], offStreetMultiplier, doorBlocked);
      if (straight <= (prefix[j] - prefix[anchor]) + 1e-6) { next = j; break; }
    }
    out.push(coordinates[next]);
    anchor = next;
  }
  return out;
}

// Admissible heuristic: straight-line metres at the minimum (street) cost of 1.
function heuristic(cell, goal, cellSizeMeters) {
  const dcol = cell.col - goal.col;
  const drow = cell.row - goal.row;
  return Math.hypot(dcol, drow) * cellSizeMeters;
}

// Drop interior points that lie on the straight line between their neighbours,
// so the polyline carries corners, not every grid step.
function collapseCollinear(points) {
  if (points.length <= 2) return points;
  const out = [points[0]];
  for (let i = 1; i < points.length - 1; i += 1) {
    const prev = out[out.length - 1];
    const here = points[i];
    const next = points[i + 1];
    const cross = (here[0] - prev[0]) * (next[1] - prev[1]) - (here[1] - prev[1]) * (next[0] - prev[0]);
    if (Math.abs(cross) > 1e-12) out.push(here);
  }
  out.push(points[points.length - 1]);
  return out;
}

// --- POI doorsteps --------------------------------------------------------

function outerRing(building) {
  const geometry = building?.geometry;
  if (!geometry) return null;
  if (geometry.type === "Polygon") return geometry.coordinates[0];
  if (geometry.type === "MultiPolygon") return geometry.coordinates[0][0];
  return null;
}

// The walkable cells hugging a building's footprint — its "doorsteps". A survivor
// enters from whichever of these is nearest, so approaching from the back of
// the building lets you enter at the back instead of trekking around to one
// fixed spot. Computed once per zone (static). Returns cell-centre coordinates.
export function buildingDoorsteps(grid, building) {
  const ring = outerRing(building);
  if (!ring) return [];
  let minLon = Infinity; let maxLon = -Infinity; let minLat = Infinity; let maxLat = -Infinity;
  for (const [lon, lat] of ring) {
    if (lon < minLon) minLon = lon;
    if (lon > maxLon) maxLon = lon;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const lo = grid.coordToCell([minLon, minLat]);
  const hi = grid.coordToCell([maxLon, maxLat]);
  const doorsteps = [];
  for (let col = lo.col - 2; col <= hi.col + 2; col += 1) {
    for (let row = lo.row - 2; row <= hi.row + 2; row += 1) {
      if (grid.classAt(col, row) === CELL.blocked) continue; // must be walkable
      // Adjacent to this building if a neighbouring cell centre falls inside it.
      let adjacent = false;
      for (const [dcol, drow] of NEIGHBORS) {
        if (pointInBuildings(grid.cellToCoord(col + dcol, row + drow), [building])) { adjacent = true; break; }
      }
      if (adjacent) doorsteps.push(grid.cellToCoord(col, row));
    }
  }
  return doorsteps;
}

// --- Minimal binary min-heap (priority = f-score) -------------------------

class MinHeap {
  constructor() { this.keys = []; this.values = []; }
  size() { return this.values.length; }
  push(key, value) {
    this.keys.push(key);
    this.values.push(value);
    let i = this.values.length - 1;
    while (i > 0) {
      const parent = (i - 1) >> 1;
      if (this.keys[parent] <= this.keys[i]) break;
      this.swap(i, parent);
      i = parent;
    }
  }
  pop() {
    const top = this.values[0];
    const lastKey = this.keys.pop();
    const lastValue = this.values.pop();
    if (this.values.length) {
      this.keys[0] = lastKey;
      this.values[0] = lastValue;
      let i = 0;
      const n = this.values.length;
      while (true) {
        const left = 2 * i + 1;
        const right = 2 * i + 2;
        let smallest = i;
        if (left < n && this.keys[left] < this.keys[smallest]) smallest = left;
        if (right < n && this.keys[right] < this.keys[smallest]) smallest = right;
        if (smallest === i) break;
        this.swap(i, smallest);
        i = smallest;
      }
    }
    return top;
  }
  swap(i, j) {
    [this.keys[i], this.keys[j]] = [this.keys[j], this.keys[i]];
    [this.values[i], this.values[j]] = [this.values[j], this.values[i]];
  }
}
